import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as Types from '@/types'
import { apiClient } from '@/services/api'

interface MaterialsState {
  materialRequests: {
    items: Types.MaterialRequest[]
    current: Types.MaterialRequest | null
    total: number
    page: number
    pageSize: number
    filters: {
      operation_id?: string
      status?: string
    }
    isLoading: boolean
    error: string | null
  }
  shippingLists: {
    items: Types.ShippingList[]
    current: Types.ShippingList | null
    total: number
    page: number
    pageSize: number
    filters: {
      material_request_id?: string
      shipping_type?: string
    }
    isLoading: boolean
    error: string | null
  }
  toolstrings: {
    items: Types.Toolstring[]
    current: Types.Toolstring | null
    total: number
    page: number
    pageSize: number
    filters: {
      operation_id?: string
      is_active?: boolean
    }
    isLoading: boolean
    error: string | null
  }
}

const initialState: MaterialsState = {
  materialRequests: {
    items: [],
    current: null,
    total: 0,
    page: 1,
    pageSize: 20,
    filters: {},
    isLoading: false,
    error: null,
  },
  shippingLists: {
    items: [],
    current: null,
    total: 0,
    page: 1,
    pageSize: 20,
    filters: {},
    isLoading: false,
    error: null,
  },
  toolstrings: {
    items: [],
    current: null,
    total: 0,
    page: 1,
    pageSize: 20,
    filters: {},
    isLoading: false,
    error: null,
  },
}

// Material Request async thunks
export const fetchMaterialRequests = createAsyncThunk(
  'materials/fetchMaterialRequests',
  async (
    { page, limit, filters }: { page?: number; limit?: number; filters?: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 20) : 0
      const response = await apiClient.listMaterialRequests({
        skip,
        limit,
        ...filters,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch material requests')
    }
  }
)

export const fetchMaterialRequest = createAsyncThunk(
  'materials/fetchMaterialRequest',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getMaterialRequest(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch material request')
    }
  }
)

export const createMaterialRequest = createAsyncThunk(
  'materials/createMaterialRequest',
  async (request: Types.MaterialRequestCreateRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.createMaterialRequest(request)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create material request')
    }
  }
)

export const updateMaterialRequest = createAsyncThunk(
  'materials/updateMaterialRequest',
  async (
    { id, updates }: { id: string; updates: Partial<Types.MaterialRequest> },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.updateMaterialRequest(id, updates)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update material request')
    }
  }
)

export const submitMaterialRequest = createAsyncThunk(
  'materials/submitMaterialRequest',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.submitMaterialRequest(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to submit material request')
    }
  }
)

export const approveMaterialRequest = createAsyncThunk(
  'materials/approveMaterialRequest',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.approveMaterialRequest(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to approve material request')
    }
  }
)

// Shipping List async thunks
export const fetchShippingLists = createAsyncThunk(
  'materials/fetchShippingLists',
  async (
    { page, limit, filters }: { page?: number; limit?: number; filters?: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 20) : 0
      const response = await apiClient.listShippingLists({
        skip,
        limit,
        ...filters,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch shipping lists')
    }
  }
)

export const fetchShippingList = createAsyncThunk(
  'materials/fetchShippingList',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getShippingList(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch shipping list')
    }
  }
)

export const createShippingList = createAsyncThunk(
  'materials/createShippingList',
  async (request: Types.ShippingListCreateRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.createShippingList(request)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create shipping list')
    }
  }
)

export const updateShippingList = createAsyncThunk(
  'materials/updateShippingList',
  async (
    { id, updates }: { id: string; updates: Partial<Types.ShippingList> },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.updateShippingList(id, updates)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update shipping list')
    }
  }
)

// Toolstring async thunks
export const fetchToolstrings = createAsyncThunk(
  'materials/fetchToolstrings',
  async (
    { page, limit, filters }: { page?: number; limit?: number; filters?: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 20) : 0
      const response = await apiClient.listToolstrings({
        skip,
        limit,
        ...filters,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch toolstrings')
    }
  }
)

export const fetchToolstring = createAsyncThunk(
  'materials/fetchToolstring',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getToolstring(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch toolstring')
    }
  }
)

export const createToolstring = createAsyncThunk(
  'materials/createToolstring',
  async (request: Types.ToolstringCreateRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.createToolstring(request)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create toolstring')
    }
  }
)

export const updateToolstring = createAsyncThunk(
  'materials/updateToolstring',
  async (
    { id, updates }: { id: string; updates: Partial<Types.Toolstring> },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.updateToolstring(id, updates)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update toolstring')
    }
  }
)

export const logToolstringUsage = createAsyncThunk(
  'materials/logToolstringUsage',
  async (
    { toolstringId, usage }: { toolstringId: string; usage: Types.ToolstringUsageLog },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.logToolstringUsage(toolstringId, usage)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to log toolstring usage')
    }
  }
)

const materialsSlice = createSlice({
  name: 'materials',
  initialState,
  reducers: {
    clearError: (state) => {
      state.materialRequests.error = null
      state.shippingLists.error = null
      state.toolstrings.error = null
    },
    setMaterialRequestFilters: (state, action) => {
      state.materialRequests.filters = action.payload
      state.materialRequests.page = 1
    },
    setShippingListFilters: (state, action) => {
      state.shippingLists.filters = action.payload
      state.shippingLists.page = 1
    },
    setToolstringFilters: (state, action) => {
      state.toolstrings.filters = action.payload
      state.toolstrings.page = 1
    },
  },
  extraReducers: (builder) => {
    // Material Requests
    builder
      .addCase(fetchMaterialRequests.pending, (state) => {
        state.materialRequests.isLoading = true
        state.materialRequests.error = null
      })
      .addCase(fetchMaterialRequests.fulfilled, (state, action) => {
        state.materialRequests.isLoading = false
        state.materialRequests.items = action.payload.items
        state.materialRequests.total = action.payload.total
        state.materialRequests.page = action.payload.page
        state.materialRequests.pageSize = action.payload.page_size
      })
      .addCase(fetchMaterialRequests.rejected, (state, action) => {
        state.materialRequests.isLoading = false
        state.materialRequests.error = action.payload as string
      })

    builder
      .addCase(fetchMaterialRequest.pending, (state) => {
        state.materialRequests.isLoading = true
        state.materialRequests.error = null
      })
      .addCase(fetchMaterialRequest.fulfilled, (state, action) => {
        state.materialRequests.isLoading = false
        state.materialRequests.current = action.payload
      })
      .addCase(fetchMaterialRequest.rejected, (state, action) => {
        state.materialRequests.isLoading = false
        state.materialRequests.error = action.payload as string
      })

    builder
      .addCase(createMaterialRequest.pending, (state) => {
        state.materialRequests.isLoading = true
        state.materialRequests.error = null
      })
      .addCase(createMaterialRequest.fulfilled, (state, action) => {
        state.materialRequests.isLoading = false
        state.materialRequests.items.unshift(action.payload)
        state.materialRequests.current = action.payload
      })
      .addCase(createMaterialRequest.rejected, (state, action) => {
        state.materialRequests.isLoading = false
        state.materialRequests.error = action.payload as string
      })

    builder
      .addCase(updateMaterialRequest.pending, (state) => {
        state.materialRequests.isLoading = true
        state.materialRequests.error = null
      })
      .addCase(updateMaterialRequest.fulfilled, (state, action) => {
        state.materialRequests.isLoading = false
        const index = state.materialRequests.items.findIndex((r) => r.id === action.payload.id)
        if (index !== -1) {
          state.materialRequests.items[index] = action.payload
        }
        state.materialRequests.current = action.payload
      })
      .addCase(updateMaterialRequest.rejected, (state, action) => {
        state.materialRequests.isLoading = false
        state.materialRequests.error = action.payload as string
      })

    builder
      .addCase(submitMaterialRequest.pending, (state) => {
        state.materialRequests.isLoading = true
      })
      .addCase(submitMaterialRequest.fulfilled, (state, action) => {
        state.materialRequests.isLoading = false
        const index = state.materialRequests.items.findIndex((r) => r.id === action.payload.id)
        if (index !== -1) {
          state.materialRequests.items[index] = action.payload
        }
        state.materialRequests.current = action.payload
      })
      .addCase(submitMaterialRequest.rejected, (state, action) => {
        state.materialRequests.isLoading = false
        state.materialRequests.error = action.payload as string
      })

    builder
      .addCase(approveMaterialRequest.pending, (state) => {
        state.materialRequests.isLoading = true
      })
      .addCase(approveMaterialRequest.fulfilled, (state, action) => {
        state.materialRequests.isLoading = false
        const index = state.materialRequests.items.findIndex((r) => r.id === action.payload.id)
        if (index !== -1) {
          state.materialRequests.items[index] = action.payload
        }
        state.materialRequests.current = action.payload
      })
      .addCase(approveMaterialRequest.rejected, (state, action) => {
        state.materialRequests.isLoading = false
        state.materialRequests.error = action.payload as string
      })

    // Shipping Lists
    builder
      .addCase(fetchShippingLists.pending, (state) => {
        state.shippingLists.isLoading = true
        state.shippingLists.error = null
      })
      .addCase(fetchShippingLists.fulfilled, (state, action) => {
        state.shippingLists.isLoading = false
        state.shippingLists.items = action.payload.items
        state.shippingLists.total = action.payload.total
        state.shippingLists.page = action.payload.page
        state.shippingLists.pageSize = action.payload.page_size
      })
      .addCase(fetchShippingLists.rejected, (state, action) => {
        state.shippingLists.isLoading = false
        state.shippingLists.error = action.payload as string
      })

    builder
      .addCase(fetchShippingList.pending, (state) => {
        state.shippingLists.isLoading = true
        state.shippingLists.error = null
      })
      .addCase(fetchShippingList.fulfilled, (state, action) => {
        state.shippingLists.isLoading = false
        state.shippingLists.current = action.payload
      })
      .addCase(fetchShippingList.rejected, (state, action) => {
        state.shippingLists.isLoading = false
        state.shippingLists.error = action.payload as string
      })

    builder
      .addCase(createShippingList.pending, (state) => {
        state.shippingLists.isLoading = true
        state.shippingLists.error = null
      })
      .addCase(createShippingList.fulfilled, (state, action) => {
        state.shippingLists.isLoading = false
        state.shippingLists.items.unshift(action.payload)
        state.shippingLists.current = action.payload
      })
      .addCase(createShippingList.rejected, (state, action) => {
        state.shippingLists.isLoading = false
        state.shippingLists.error = action.payload as string
      })

    builder
      .addCase(updateShippingList.pending, (state) => {
        state.shippingLists.isLoading = true
        state.shippingLists.error = null
      })
      .addCase(updateShippingList.fulfilled, (state, action) => {
        state.shippingLists.isLoading = false
        const index = state.shippingLists.items.findIndex((s) => s.id === action.payload.id)
        if (index !== -1) {
          state.shippingLists.items[index] = action.payload
        }
        state.shippingLists.current = action.payload
      })
      .addCase(updateShippingList.rejected, (state, action) => {
        state.shippingLists.isLoading = false
        state.shippingLists.error = action.payload as string
      })

    // Toolstrings
    builder
      .addCase(fetchToolstrings.pending, (state) => {
        state.toolstrings.isLoading = true
        state.toolstrings.error = null
      })
      .addCase(fetchToolstrings.fulfilled, (state, action) => {
        state.toolstrings.isLoading = false
        state.toolstrings.items = action.payload.items
        state.toolstrings.total = action.payload.total
        state.toolstrings.page = action.payload.page
        state.toolstrings.pageSize = action.payload.page_size
      })
      .addCase(fetchToolstrings.rejected, (state, action) => {
        state.toolstrings.isLoading = false
        state.toolstrings.error = action.payload as string
      })

    builder
      .addCase(fetchToolstring.pending, (state) => {
        state.toolstrings.isLoading = true
        state.toolstrings.error = null
      })
      .addCase(fetchToolstring.fulfilled, (state, action) => {
        state.toolstrings.isLoading = false
        state.toolstrings.current = action.payload
      })
      .addCase(fetchToolstring.rejected, (state, action) => {
        state.toolstrings.isLoading = false
        state.toolstrings.error = action.payload as string
      })

    builder
      .addCase(createToolstring.pending, (state) => {
        state.toolstrings.isLoading = true
        state.toolstrings.error = null
      })
      .addCase(createToolstring.fulfilled, (state, action) => {
        state.toolstrings.isLoading = false
        state.toolstrings.items.unshift(action.payload)
        state.toolstrings.current = action.payload
      })
      .addCase(createToolstring.rejected, (state, action) => {
        state.toolstrings.isLoading = false
        state.toolstrings.error = action.payload as string
      })

    builder
      .addCase(updateToolstring.pending, (state) => {
        state.toolstrings.isLoading = true
        state.toolstrings.error = null
      })
      .addCase(updateToolstring.fulfilled, (state, action) => {
        state.toolstrings.isLoading = false
        const index = state.toolstrings.items.findIndex((t) => t.id === action.payload.id)
        if (index !== -1) {
          state.toolstrings.items[index] = action.payload
        }
        state.toolstrings.current = action.payload
      })
      .addCase(updateToolstring.rejected, (state, action) => {
        state.toolstrings.isLoading = false
        state.toolstrings.error = action.payload as string
      })

    builder
      .addCase(logToolstringUsage.pending, (state) => {
        state.toolstrings.isLoading = true
      })
      .addCase(logToolstringUsage.fulfilled, (state) => {
        state.toolstrings.isLoading = false
      })
      .addCase(logToolstringUsage.rejected, (state, action) => {
        state.toolstrings.isLoading = false
        state.toolstrings.error = action.payload as string
      })
  },
})

export const { clearError, setMaterialRequestFilters, setShippingListFilters, setToolstringFilters } =
  materialsSlice.actions
export default materialsSlice.reducer
